import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CartesianGrid, Legend, Line, LineChart, Tooltip, XAxis, YAxis } from 'recharts';
import { FileDown, ImageDown } from 'lucide-react';
import SidebarLayout from '../../components/SidebarLayout';
import { downloadPdf, downloadPng } from './exportCanvas';
import { analyticsTableService } from '../../services/api';
import { MIN_ASSESSMENTS, buildAcademics } from './academics';
import {
  AXIS, GRID, PLUM, PLUM_LIGHT, RUST,
  BarCard, Card, HeatMapCard, PieCard, pctTick
} from './charts';
import { AdmissionsPanel, AttendancePanel, PaymentsPanel, ReceivablesPanel } from './panels';
import { FilterPanel, toQueryFilters, useFilterValues } from './filters';
import { passesFilters } from './localAggregate';
import styles from './NativeVisuals.module.css';

const TEMPLATES = [
  { key: 'Academics', label: 'Academics' },
  { key: 'Attendance', label: 'Attendance' },
  { key: 'Admissions', label: 'Admissions' },
  { key: 'Receivables', label: 'Receivables' },
  { key: 'Payments', label: 'Payments' }
];

/* Academics reads raw rows rather than server totals. An average of
   per-assessment percentages is not the same number as total marks over total
   maximum, and the first is what a school means by an average score. At 2,330
   rows it fits comfortably; the other four do not, which is why they
   aggregate on the server. */
const AcademicsPanel = ({ filters }) => {
  const [state, setState] = useState({ status: 'loading' });

  const load = useCallback((signal) => {
    setState({ status: 'loading' });
    analyticsTableService
      .fetchAll('Academics', { signal })
      .then((result) => {
        if (signal?.aborted) return;
        setState({ status: 'ready', ...result });
      })
      .catch((error) => {
        if (signal?.aborted || error.name === 'AbortError') return;
        setState({ status: 'error', message: error.message || 'Could not load Academics.' });
      });
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const filterKey = JSON.stringify(filters || []);
  const model = useMemo(() => {
    if (state.status !== 'ready') return null;
    const active = JSON.parse(filterKey);
    const rows = active.length
      ? (state.rows || []).filter((row) => passesFilters(row, active))
      : state.rows || [];
    return buildAcademics(rows);
  }, [state, filterKey]);

  if (state.status === 'loading') {
    return (
      <div className={styles.canvas} data-export-canvas>
        <div className={styles.state}>
          <h2 className={styles.stateTitle}>Loading Academics…</h2>
          <p className={styles.stateText}>Reading your records and working out the figures.</p>
        </div>
      </div>
    );
  }

  if (state.status === 'error') {
    return (
      <div className={styles.canvas} data-export-canvas>
        <div className={`${styles.state} ${styles.errorState}`}>
        <h2 className={styles.stateTitle}>Academics could not be loaded</h2>
        <p className={styles.stateText}>{state.message}</p>
          <button type="button" className={styles.retry} onClick={() => load()}>Try again</button>
        </div>
      </div>
    );
  }

  if (!model) {
    return (
      <div className={styles.canvas} data-export-canvas>
        <div className={styles.state}>
          <h2 className={styles.stateTitle}>No scored assessments yet</h2>
        <p className={styles.stateText}>
          Academics has {(state.total || 0).toLocaleString()} records, but none carry both a
            mark and a maximum, so there is nothing to chart.
          </p>
        </div>
      </div>
    );
  }

  const subjectNames = model.subjects.map((s) => s.subject);
  const typeNames = model.assessmentTypes.map((t) => t.type);
  // The bar chart ranks by score; the line chart holds a fixed order so the
  // shape of the two terms can be compared across subjects rather than read
  // as a slope.
  const alphabetical = [...model.subjects].sort((a, b) => a.subject.localeCompare(b.subject));

  return (
    <>
      <div className={styles.canvas} data-export-canvas>
      <div className={styles.kpiRow}>
        {[
          { label: 'Students', value: model.students },
          { label: 'Subjects', value: model.subjectCount },
          { label: 'Assessments', value: model.assessments.toLocaleString() },
          { label: 'Average score', value: `${model.overallAvg}%` }
        ].map((kpi) => (
          <div key={kpi.label} className={styles.kpi}>
            <span className={styles.kpiLabel}>{kpi.label}</span>
            <span className={styles.kpiValue}>{kpi.value}</span>
          </div>
        ))}
      </div>

      <div className={styles.grid} style={{ '--cols': 2 }}>
        <BarCard
          wide
          title="Average score by subject"
          note="As a percentage of each test's maximum, because the maximum varies by test."
          data={model.subjects}
          xKey="subject"
          angled
          yTickFormatter={pctTick}
          tooltipFormatter={(v) => (v === null ? 'No data' : `${v}%`)}
          series={[
            { key: 'term1', name: 'Term 1', colour: PLUM_LIGHT },
            { key: 'term2', name: 'Term 2', colour: PLUM }
          ]}
        />

        <HeatMapCard
          wide={false}
          title="Subject against assessment type"
          note="Average score in each combination. Darker is higher."
          rows={subjectNames}
          columns={typeNames}
          valueAt={(subject, type) => {
            const v = model.gridValues.get(`${subject}|${type}`);
            return v === undefined ? null : v;
          }}
          formatValue={(v) => v}
          unit="%"
        />

        <PieCard
          title="What the assessments are"
          note="How the year divides between continuous work and formal examinations."
          data={model.assessmentTypes.map((t) => ({ name: t.type, value: t.n }))}
          valueFormatter={(v) => `${v.toLocaleString()} marks`}
          centreValue={model.assessments.toLocaleString()}
          centreLabel="assessments"
        />

        <BarCard
          title="Where the students sit"
          note="Each student's own average across everything they took."
          data={model.bands}
          xKey="band"
          yTickFormatter={(v) => v}
          tooltipFormatter={(v) => `${v} students`}
          series={[{ key: 'students', name: 'Students', colour: PLUM }]}
          colourBy={(row) => (row.from < 60 ? RUST : PLUM)}
        />

        {model.slope.length > 1 ? (
          <Card
            title="Term 1 against Term 2, across the subjects"
            note="Subjects run along the axis in a fixed order, so the gap between the two terms reads as one shape."
          >
            <LineChart data={alphabetical} margin={{ top: 8, right: 16, bottom: 4, left: 0 }}>
              <CartesianGrid stroke={GRID} vertical={false} />
              <XAxis
                dataKey="subject"
                tick={{ fill: AXIS, fontSize: 10 }}
                interval={0}
                angle={-18}
                textAnchor="end"
                height={66}
              />
              <YAxis
                domain={['dataMin - 3', 'dataMax + 3']}
                tickFormatter={pctTick}
                tick={{ fill: AXIS, fontSize: 11 }}
                width={46}
              />
              <Tooltip formatter={(v) => (v === null ? 'No data' : `${v}%`)} />
              <Legend wrapperStyle={{ fontSize: 10 }} />
              <Line
                type="monotone"
                dataKey="term1"
                name="Term 1"
                stroke={PLUM_LIGHT}
                strokeWidth={2}
                dot={{ r: 3 }}
                connectNulls
              />
              <Line
                type="monotone"
                dataKey="term2"
                name="Term 2"
                stroke={PLUM}
                strokeWidth={2}
                dot={{ r: 3 }}
                connectNulls
              />
            </LineChart>
          </Card>
        ) : null}
      </div>
      </div>

      <div className={styles.caveat}>
        <h4 className={styles.caveatTitle}>Worth knowing about this data</h4>
        <ul>
          <li>
            Scores are percentages. Raw marks cannot be compared across tests because the
            maximum ranges from 5 to 80.
          </li>
          {model.thinSubjects.length > 0 ? (
            <li>
              {model.thinSubjects.length} subject
              {model.thinSubjects.length === 1 ? ' is' : 's are'} held out of the subject
              comparison for having fewer than {MIN_ASSESSMENTS} assessments
              ({model.thinSubjects.map((s) => `${s.subject}, ${s.n}`).join('; ')}).
            </li>
          ) : null}
          <li>
            Assessment names arrive in mixed case — &ldquo;Annual Exam&rdquo; and &ldquo;ANNUAL
            EXAM&rdquo; are one thing. They are folded together here, but the records are worth
            cleaning.
          </li>
          <li>
            Class, section and academic year each hold a single value, and there are only three
            test dates, so none of them can carry a comparison.
          </li>
        </ul>
      </div>
    </>
  );
};

const PANELS = {
  Academics: AcademicsPanel,
  Attendance: AttendancePanel,
  Admissions: AdmissionsPanel,
  Receivables: ReceivablesPanel,
  Payments: PaymentsPanel
};

const NativeVisualsPage = () => {
  const [active, setActive] = useState('Academics');
  const { byTemplate, set, clear } = useFilterValues();
  const values = byTemplate[active] || {};
  const queryFilters = toQueryFilters(active, values);
  const [busy, setBusy] = useState('');
  const [exportError, setExportError] = useState('');
  const holderRef = useRef(null);
  const Panel = PANELS[active] || AcademicsPanel;

  const exportAs = async (kind) => {
    const holder = holderRef.current;
    const node = holder?.querySelector('[data-export-canvas]') || holder;
    if (!node) return;

    setBusy(kind);
    setExportError('');
    try {
      if (kind === 'png') {
        await downloadPng(node, `analyticax-${active.toLowerCase()}`);
      } else {
        await downloadPdf(node, `analyticax-${active.toLowerCase()}`, `${active} — Dashboards`);
      }
    } catch (error) {
      setExportError('That export did not work. Try again, or take a screenshot.');
    } finally {
      setBusy('');
    }
  };

  return (
    <SidebarLayout>
      <div className={styles.page}>
        <div className={styles.head}>
          <div>
            <h1 className={styles.title}>Dashboards</h1>
            <p className={styles.subtitle}>
              Charts drawn from your template data, alongside the Power BI reports.
            </p>
          </div>

          <div className={styles.tools}>
            {exportError ? <span className={styles.toolNote}>{exportError}</span> : null}
            <button
              type="button"
              className={styles.toolBtn}
              onClick={() => exportAs('png')}
              disabled={Boolean(busy)}
            >
              <ImageDown size={14} strokeWidth={2} aria-hidden="true" />
              {busy === 'png' ? 'Preparing…' : 'Download image'}
            </button>
            <button
              type="button"
              className={styles.toolBtn}
              onClick={() => exportAs('pdf')}
              disabled={Boolean(busy)}
            >
              <FileDown size={14} strokeWidth={2} aria-hidden="true" />
              {busy === 'pdf' ? 'Preparing…' : 'Export PDF'}
            </button>
          </div>
        </div>

        <FilterPanel
          template={active}
          values={values}
          onChange={set}
          onClear={clear}
        />

        <div className={styles.tabBar} role="tablist" aria-label="Template">
          {TEMPLATES.map((t) => (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={active === t.key}
              className={active === t.key ? `${styles.tab} ${styles.tabActive}` : styles.tab}
              onClick={() => setActive(t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Keyed so switching tabs unmounts the old panel and aborts its requests */}
        <div ref={holderRef}>
          <Panel key={active} filters={queryFilters} />
        </div>
      </div>
    </SidebarLayout>
  );
};

export default NativeVisualsPage;
